import React, { useEffect, useMemo, useState } from "react";
import Spinner from "../../../shared/Spinner";
import { formatDate } from "../../../utils";
import request, { renderQueries } from "../../../utils/request";
import { buildOperationalUtcWindow } from "../../../utils/date.utils";

/**
 * Manual leg multi-select for Manifest V2 create.
 *
 * @param {object} props
 * @param {string} props.driverId
 * @param {string} props.parentCompany
 * @param {string} props.startDate
 * @param {string} props.endDate
 * @param {string[]} props.selectedLegIds
 * @param {function} props.onChange
 */
const ManifestV2LegPicker = ({
  driverId,
  parentCompany,
  startDate,
  endDate,
  selectedLegIds = [],
  onChange,
}) => {
  const [legs, setLegs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedSet = useMemo(
    () => new Set((selectedLegIds || []).map((id) => String(id))),
    [selectedLegIds]
  );

  useEffect(() => {
    async function loadLegs() {
      if (!driverId || !startDate || !endDate || !parentCompany) {
        setLegs([]);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const manifestWindow = buildOperationalUtcWindow(startDate, endDate);
        const query = {
          parent: parentCompany,
          driverId,
          manifestId: null,
          manifestV2Id: null,
          route: true,
          startDate: manifestWindow.startDate,
          endDate: manifestWindow.endDate,
          limit: 200,
        };

        const res = await request(`/legs?${renderQueries(query)}`);
        setLegs(res.data?.data || res.data?.legs || []);
      } catch (err) {
        setError(err?.data?.message || "Unable to load legs");
        setLegs([]);
      } finally {
        setLoading(false);
      }
    }

    loadLegs();
  }, [driverId, parentCompany, startDate, endDate]);

  function toggleLeg(legId) {
    const id = String(legId);
    const next = selectedSet.has(id)
      ? selectedLegIds.filter((entry) => String(entry) !== id)
      : [...selectedLegIds, id];
    onChange(next);
  }

  if (!driverId || !startDate || !endDate) {
    return (
      <p className="text-sm text-gray-500">
        Select a driver and date range to choose legs manually.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
        <Spinner size="sm" />
        Loading available legs...
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (!legs.length) {
    return (
      <p className="text-sm text-gray-500">
        No unmanifested legs found for this driver in the selected window.
      </p>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-gray-200 bg-white p-3">
      <p className="text-sm text-gray-600">
        Select legs to include ({selectedLegIds.length} selected)
      </p>
      <ul className="max-h-64 space-y-2 overflow-y-auto">
        {legs.map((leg) => {
          const legId = String(leg._id);
          const checked = selectedSet.has(legId);
          return (
            <li key={legId}>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-100 p-2 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checked}
                  onChange={() => toggleLeg(legId)}
                />
                <span className="min-w-0 text-sm">
                  <span className="font-medium text-gray-900">
                    {leg.loadNumber || leg.orderNumber || legId}
                  </span>
                  <span className="block text-gray-500">
                    Appt {formatDate(leg.appointmentDate, null, true)} · BOL{" "}
                    {leg.shipping?.bolNumber || "—"}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ManifestV2LegPicker;
